'use client';
import { useTranslations } from 'next-intl';
import { BookOpen, Plus } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { Logo } from './logo';
import { LanguageSwitcher } from './language-switcher';
import { AuthMenu } from './auth-menu';
import { useMe } from '@/hooks/use-me';
import { LinkIcon } from '@/components/ui/link-status';

export function SiteHeader({ showNav = true }: { showNav?: boolean }) {
  const t = useTranslations('nav');
  const { data, refresh } = useMe();

  return (
    <header className="sticky top-0 z-(--z-sticky) border-b border-line bg-paper/85 backdrop-blur">
      {/* First tab stop on every page: jump past the chrome straight to the content. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-(--z-popover) focus:rounded-md focus:bg-accent focus:px-3 focus:py-2 focus:text-sm focus:text-accent-fg"
      >
        {t('skipToContent')}
      </a>
      <div className="mx-auto flex h-14 max-w-6xl items-center gap-2 px-gutter lg:px-gutter-lg">
        <Link href="/" className="flex items-center gap-2 rounded-md" aria-label={t('home')}>
          <Logo size="sm" compact />
        </Link>

        {showNav ? (
          <nav aria-label={t('home')} className="ms-4 hidden items-center gap-1 sm:flex">
            <Link
              href="/albums"
              className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-sm text-ink-2 hover:bg-paper-2 hover:text-ink"
            >
              <LinkIcon><BookOpen aria-hidden className="size-4" /></LinkIcon>
              {t('myAlbums')}
            </Link>
          </nav>
        ) : null}

        <div className="ms-auto flex items-center gap-1">
          {showNav ? (
            <Link
              href="/albums/new"
              className="hidden h-9 items-center gap-1.5 rounded-md bg-accent px-3 text-sm font-medium text-accent-fg shadow-xs hover:bg-accent-hover sm:inline-flex"
            >
              <LinkIcon><Plus aria-hidden className="size-4" /></LinkIcon>
              {t('newAlbum')}
            </Link>
          ) : null}
          <LanguageSwitcher compact />
          <AuthMenu me={data} onChanged={refresh} />
        </div>
      </div>
    </header>
  );
}
